import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { DayState, DayStateRange } from '../rental-billing.model';
import type { SimulationState } from '../rental-billing.service';
import type { RentalLifecycleStore } from '../rental-lifecycle.store';
import { daysBetween, frWeekday, money, qtyFr, signedMoney } from '../rental-days';

interface EffectView {
  due: string | null;
  credit: string | null;
  net: { ht: string; ttc: string } | null;
  dayCounts: string[];
  sales: string[];
  blocked: { cls: 'err' | 'warn'; text: string } | null;
  details: string[];
}

// Jours d'un état dans les séries du serveur : chaque série va du start au end, les deux inclus.
function countDays(days: DayStateRange[] | null | undefined, state: DayState): number {
  return (days ?? []).filter((r) => r.state === state).reduce((n, r) => n + Math.max(0, daysBetween(r.start, r.end) + 1), 0);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`;
}

// Effet chiffré d'une décision : champs du serveur affichés tels quels ; seuls des jours d'états rendus sont comptés.
@Component({
  selector: 'app-rental-effect',
  templateUrl: './rental-effect.component.html',
  styleUrls: ['./rental-effect.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinnerModule],
})
export class RentalEffectComponent implements OnChanges {
  @Input() state: SimulationState = { loading: false, view: null, error: null };
  @Input() rangeText: string | null = null;
  @Input() warnings: string[] = [];
  @Input() errors: string[] = [];
  @Input() scope: string | null = null; // « · Lit médicalisé, Matelas à air »
  @Input() focusLineIds: number[] = [];
  @Input() store: RentalLifecycleStore | null = null; // facultatif : noms des matériels du détail

  vm: EffectView = { due: null, credit: null, net: null, dayCounts: [], sales: [], blocked: null, details: [] };

  ngOnChanges(): void {
    this.vm = this.build();
  }

  private build(): EffectView {
    const v = this.state?.view ?? null;
    const vm: EffectView = { due: null, credit: null, net: null, dayCounts: [], sales: [], blocked: null, details: [] };
    if (!v) return vm;
    const b = v.order_before;
    const a = v.order_after;
    vm.due = `Au ${frWeekday(v.as_of)}, à facturer au prochain passage : ${money(b.due_ht)} → ${money(a.due_ht)} HT`;
    if (b.credit_ht !== 0 || a.credit_ht !== 0) {
      const refs = [...new Set(v.lines.flatMap((l) => l.after.credits.map((c) => c.corrects_doc_ref)).filter((r): r is string => !!r))];
      vm.credit = `Avoirs au prochain passage : ${money(b.credit_ht)} → ${money(a.credit_ht)} HT` + (refs.length ? ` (${refs.join(', ')})` : '');
    }
    vm.net = { ht: signedMoney(v.order_delta.net_ht), ttc: signedMoney(v.order_delta.net_ttc) };
    const focus = this.focusLineIds?.length ? new Set(this.focusLineIds) : null;
    const touched = v.lines.filter((l) => !focus || focus.has(l.line_id));
    const delta = (state: DayState) => touched.reduce((n, l) => n + countDays(l.after.days, state) - countDays(l.before.days, state), 0);
    const compensated = delta('COMPENSATED');
    if (compensated > 0) vm.dayCounts.push(`${plural(compensated, 'jour déjà payé reporté', 'jours déjà payés reportés')} sur les périodes suivantes`);
    const paidInterrupted = delta('PAID_INTERRUPTED');
    if (paidInterrupted > 0) vm.dayCounts.push(`${plural(paidInterrupted, 'jour interrompu', 'jours interrompus')} dans une période due en entier`);
    for (const s of v.sales) {
      const who = v.sales.length > 1 ? `${s.after.product_name ?? 'ligne ' + s.line_id} : ` : '';
      vm.sales.push(`${who}Reste à régler : ${qtyFr(s.before.to_settle_qty)} → ${qtyFr(s.after.to_settle_qty)}`);
    }
    if (a.refused) {
      vm.blocked = { cls: 'err', text: `Passage bloqué : ${a.refused}` };
    } else if (!a.complete) {
      const shown = a.blocking.slice(0, 3).join(' · ');
      vm.blocked = { cls: 'warn', text: 'Non chiffré : ' + (shown || 'ligne bloquée') + (a.blocking.length > 3 ? ' …' : '') };
    }
    const name = (id: number) => this.store?.line(id)?.product_name ?? `ligne ${id}`;
    for (const l of v.lines) {
      vm.details.push(l.before.amounts.complete && l.after.amounts.complete
        ? `${name(l.line_id)} : net ${money(l.before.amounts.net_ht)} → ${money(l.after.amounts.net_ht)} HT`
        : `${name(l.line_id)} : non chiffré`);
    }
    for (const s of v.sales) {
      const label = s.after.product_name ?? `ligne ${s.line_id}`;
      vm.details.push(s.before.amounts.complete && s.after.amounts.complete
        ? `${label} : net ${money(s.before.amounts.net_ht)} → ${money(s.after.amounts.net_ht)} HT`
        : `${label} : non chiffré`);
    }
    return vm;
  }
}
